/**
 * Created by mariushirjanu on 15/06/2017.
 */
import { HttpDataService, IHttpDataCache } from '../http/http-data.service';

import { ICompany, ICompanyCountry, ICompanyExtended, IContact, ICountryExtended, IDepartment, IDescribedObject, ILocationExtended, ILov, INamedObject, ISubCompany, IWebshop } from '../../../type_definitions/datatypes';
import { Injectable } from '@angular/core';
import { TranslationService } from '../translation/translation.service';

import { RoleContactType } from '../contact/role-contact-type';

const CANCELID_COMP_DUPLICATES:string = 'checkCompanyDuplicates';
const CANCELID_WEBSHOPCOMP_DUPLICATES:string = 'checkWebshopCompanyDuplicates';

export interface ICodeDescription {
  id?:number;
  code:string;
  description:string;
}

export interface ICompanyCustomerInfo {
  totalCustomers:number;
  activeCustomers:number;
  description:string;
}

export enum CompanyType {
  BP = 'P',
  OEM = 'V',
  CUSTOMER = 'C',
  ACSL = 'A'
}

@Injectable()
export class CompanyDataService {
  private cache:IHttpDataCache<any>;

  constructor(
    private httpDataService:HttpDataService,
    private translationService:TranslationService
  ) {
    this.cache = httpDataService.createCache();
  }

  public clear() {
    this.cache.clear();
  }

  // get list of vendors
  public listVendors():Promise<ICompany[]> {
    return this.cache.doGET(
      'rest/bpprofiles/vendors',
      {},
      undefined,
      'Error loading vendors'
    );
  }

  public getVendorCompanies(restricted?:boolean, filter?:string):Promise<ICompany[]> {
    return this.cache.doGET(
      'rest/compman/vendorcompanies',
      {
        restricted,
        filter
      },
      undefined,
      'Error loading vendors'
    );
  }

  public customerCompanies():Promise<ICompany[]> {
    return this.cache.doGET('rest/compman/customercompanies', undefined, undefined, 'Error loading Customer companies');
  }

  public getSubCompanies(companyId:number):Promise<ISubCompany> {
    return this.httpDataService.doGET( // do not cache this for re-fetch in userInfo
      `rest/compman/company/${companyId}/subcompanies`,
      {},
      undefined,
      'Error loading subcompanies'
    );
  }

  public getSubCompaniesForAUser(userId:number):Promise<ISubCompany> {
    return this.cache.doGET(
      `rest/compman/subcompanies?userId=${userId}`,
      {},
      undefined,
      'Error loading subcompanies'
    );
  }

  public addSubcompany(subcompany:ISubCompany):Promise<ISubCompany> {
    return this.httpDataService.doPOST(
      `rest/compman/company/${subcompany.company.id}/subcompanies`,
      undefined,
      subcompany,
      undefined,
      'Error creating subcompany'
    );
  }

  public addCompany(company:ICompanyExtended):Promise<ICompanyExtended> {
    return this.httpDataService.doPOST(
      'rest/compman/companies',
      undefined,
      company,
      this.translationService.instant('GA.GENERAL.SAVING', { label: this.translationService.instant('GA.GENERAL.COMPANY') }),
      'Error creating company'
    );
  }

  public updateCompany(company:ICompanyExtended):Promise<ICompanyExtended> {
    return this.httpDataService.doPUT(
      `rest/compman/companies`,
      undefined,
      company,
      this.translationService.instant('GA.GENERAL.SAVING', { label: this.translationService.instant('GA.GENERAL.COMPANY') }),
      'Error updating company'
    );
  }

  public getCompany(companyId:number):Promise<ICompanyExtended> {
    return this.httpDataService.doGET(
      `rest/compman/company/${companyId}`,
      undefined,
      undefined,
      'Error loading company details'
    );
  }

  public searchCompanies(name:string, type?:string):Promise<ICompany[]> {
    return this.httpDataService.doGET(
      'rest/compman/companies',
      {
        filter: name,
        type
      },
      undefined,
      'Error getting companies',
      CANCELID_COMP_DUPLICATES
    );
  }

  public searchWebshopCompanies(name:string, type?:string):Promise<ICompany[]> {
    return this.httpDataService.doGET(
      'rest/compman/webshopcompanies',
      {
        filter: name,
        type
      },
      undefined,
      'Error getting companies',
      CANCELID_WEBSHOPCOMP_DUPLICATES
    );
  }

  public getCompanyTypes():Promise<ILov[]> {
    return this.cache.doGET(
      'rest/compman/companies/types',
      undefined,
      undefined,
      'Error loading company types'
    );
  }

  public getCompanySkins():Promise<IDescribedObject[]> {
    return this.cache.doGET(
      'rest/compman/skins',
      undefined,
      undefined,
      'Error loading skins'
    );
  }

  public getCompanyGBDMs():Promise<IContact[]> {
    return this.cache.doGET(
      'rest/compman/companies/gbdms',
      undefined,
      undefined,
      'Error loading GBDM list'
    );
  }

  public getLAMCompanyCountries():Promise<ICompanyCountry[]> {
    return this.cache.doGET(
      'rest/compman/lams/bp/companycountries',
      undefined,
      undefined,
      'Error loading LAM company countries'
    );
  }

  public getLAMCompanyDepartments():Promise<ICompanyCountry[]> {
    return this.cache.doGET(
      'rest/compman/lams/oem/departments',
      undefined,
      undefined,
      'Error loading LAM company departments'
    );
  }

  public getLAMCustomerCountries(companyId:number):Promise<ICountryExtended[]> {
    return this.cache.doGET(
      `rest/compman/company/${companyId}/countries`,
      undefined,
      undefined,
      'Error loading LAM customer countries'
    );
  }

  public getCompanyCountries(companyId:number, countryId:number):Promise<ICompanyCountry[]> {
    return this.cache.doGET(
      `rest/compman/company/${companyId}/companycountry`,
      { countryId },
      undefined,
      'Error loading company countries'
    );
  }

  public getLAMMainContacts(filter:string, lamType:string, id:number, contactType?:RoleContactType):Promise<IContact[]> { // contactType = LAM or ORDER_UPDA; if contactType not provided uses LAM
    return this.httpDataService.doGET(
      'rest/compman/lams/contacts',
      {
        filter,
        lamType,
        id,
        contactType
      },
      undefined,
      'Error searching LAM contacts'
    );
  }

  public getSuperLAM(type:string, id:number):Promise<INamedObject> {
    return this.cache.doGET(
      'rest/compman/lams/super',
      {
        type,
        id
      },
      undefined,
      'Error loading super LAM'
    );
  }

  public getCompanyCustomerInfo(lamType:string, lamId:number):Promise<ICompanyCustomerInfo> {
    return this.cache.doGET(
      `rest/compman/lamcustomerinfo`,
      {
        lamType,
        lamId
      },
      undefined,
      'Error loading customer info'
    );
  }

  public getOEMCustomerTypes(oemCompanyId:number):Promise<ICodeDescription[]> {
    return this.cache.doGET(
      `rest/compman/oems/customertypes`,
      { oemCompanyId },
      undefined,
      'Error loading oem customer type'
    );
  }

  public getVGAMContacts(filter:string, vendorCompanyId:number):Promise<IContact[]> {
    return this.cache.doGET(
      'rest/contacts/vgams',
      {
        filter,
        vendorCompanyId
      },
      undefined,
      'Error loading contacts'
    );
  }

  public getCompanyWebshops(companyId:number):Promise<IWebshop[]> {
    return this.cache.doGET(
      `rest/compman/company/${companyId}/webshops`,
      { companyId },
      undefined,
      undefined
    );
  }

  public getUserCountryDepartment():Promise<ICompanyCountry> {
    return this.cache.doGET(
      'rest/compman/department',
      undefined,
      undefined,
      'Error loading department'
    );
  }

  public getCompanyDepartments(companyId:number):Promise<IDepartment[]> {
    return this.cache.doGET(
      `rest/compman/company/${companyId}/departments`,
      undefined,
      undefined,
      'Error loading departments'
    );
  }

  public getCompanyLocations(companyId:number):Promise<ILocationExtended[]> {
    return this.cache.doGET(
      `rest/compman/company/${companyId}/locations`,
      undefined,
      undefined,
      'Error loading locations'
    );
  }

  public getCompanyContacts(filter:string, companyId:number):Promise<IContact[]> {
    return this.cache.doGET(
      `rest/compman/company/${companyId}/contacts`,
      { filter },
      undefined,
      'Error loading contacts'
    );
  }

  public getCompanySectors():Promise<ICodeDescription[]> {
    return this.cache.doGET(
      'rest/activitysectors',
      undefined,
      undefined,
      'Error loading company sectors'
    );
  }
}